import { Element } from 'hast'
import { Options, State, toMdast } from 'hast-util-to-mdast'
import { decode } from 'he'
import rehypeParse from 'rehype-parse'
import rehypeRaw from 'rehype-raw'
import rehypeRemark from 'rehype-remark'
import rehypeSanitize from 'rehype-sanitize'
import rehypeStringify from 'rehype-stringify'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import remarkStringify from 'remark-stringify'
import { unified } from 'unified'

import {
  rehypeConvertFromATagToText,
  rehypeHexColor,
  rehypeRemoveNoAttributeSpanTag,
  remarkStripHtmlComments,
} from './rehype-plugins'
import { remarkDel, remarkLists } from './remark-plugins'
import {
  allowedEditorInputSchema,
  allowedPastedMsoCellSchema,
  allowedPlainTextSchema,
} from './schema'

const KARAKURI_SKILL_RICH_TEXT_REGEX = /<p>(\[\[\/skills\/[^\]]+\]\])<\/p>/
export const KARAKURI_SKILL_PLAIN_TEXT_REGEX = /\[\[\/skills\/[^\]]+\]\]/
/**
 *  HTMLからMarkdownにMarkup言語を変換する.
 */
export const convertFromHtmlToMarkdown = (html?: string) => {
  if (!html) {
    return ''
  }
  const convertedHtml = addZeroWidthSpaceToEmptyParagraphs(html)
  /** カラクリのSkillの書式が入力された場合には、Markdownとして扱わずプレーンテキストとして扱う. */
  const match = KARAKURI_SKILL_RICH_TEXT_REGEX.exec(convertedHtml)
  if (match) {
    /** リッチテキストエディタから出力された時点でHTMLエスケープ（ & -> &amp; ）されるため、デコードする. */
    return decode(match[1])
  }
  const markdown = unifiedFromHtmlToMarkdown(convertedHtml)
  return removeZeroWidthSpace(convertFromHtmlToMarkdownLineBreaks(markdown)).trimEnd()
}

/**
 * TipTap Editor にGoogleスプレッドシートをペーストする際のフォーマット調整をする.
 *
 *  brタグを区切り文字として、一行を段落に分割する.
 *  (かなりの力技処理 & パターン網羅ができていないため、不具合に応じて修正する必要あり)
 */
export const convertFromMsoCellHtmlToHtml = (html: string) => {
  /** ExcelやGoogleスプレッドシート等の表形式からのペースト以外は処理をスキップする. */
  const MSO_CELL_PATTERN = /br\s*\{\s*mso-data-placement:same-cell;?\s*}/i
  if (!MSO_CELL_PATTERN.test(html)) return html

  /** br タグ以外を除去*/
  const paragraph = unifiedRemoveMsoCellMarkup(html)
  /** br タグを区切り文字としてParagraph を定義 */
  const chunks = paragraph.split(/<br\s*\/?>/gi)
  const wrapped = chunks.map(text => `<p>${text}</p>`)
  return wrapped.join('')
}

/**
 *  MarkdownからHTMLにMarkup言語を変換する.
 */
export const convertFromMarkdownToHtml = (markdown?: string) => {
  if (!markdown) {
    return ''
  }
  const convertedMarkdown = convertFromMarkdownToHtmlLineBreaks(markdown)
  const html = unifiedFromMarkdownToHtml(convertedMarkdown)
  return removeZeroWidthSpace(html).trimEnd()
}

/**
 * MarkdownとHTMLのMarkup言語を除去する..
 */
export const convertFromMarkdownAndHtmlToPlainText = (markupText?: string) => {
  if (!markupText) {
    return ''
  }
  const convertedMarkupText = convertFromMarkdownToHtmlLineBreaks(markupText)
  const plainText = unifiedRemoveMarkdownAndHtmlMarkup(convertedMarkupText)
  return removeZeroWidthSpace(removeMarkdownEscape(plainText)).trim()
}

/**
 *  会話カードエディタ下位互換性対応: プレーンテキストをMarkdown書式をエスケープしたMarkdownに変換する.
 *
 *  過去にPlainTextで保存されている回答文をMarkdown形式に変換する際に、Markdownの書式として使っている記号をエスケープして出力する.
 *
 * 例1: # 見出し1 -> \# 見出し1
 * 例2:  **強調** -> \*\*強調\*\*
 */
export const convertFromPlainTextToEscapedMarkdown = (text: string | undefined) => {
  if (!text) {
    return ''
  }
  const lines = text.split('\n')
  const convertedLines = lines.map((line, index) => {
    if (index === lines.length - 1) {
      return line
    } else {
      return line.trimEnd() + '\n\n'
    }
  })
  const convertText = convertedLines.join('')
  return unifiedEscapedMarkdown(convertText).trimEnd()
}

/**
 * TipTap Editor にテキストをペーストする際のフォーマット調整をする..
 */
export const convertFromPastedTextToMarkdown = (text: string) => {
  /** 空白行を空のParagraph（<p></p>）に変換するためにZWSPを挿入する. */
  return text.replace(/\n(?=\n)/g, '\n\u200B')
}

export function convertFromMarkdownToRawTextLineBreaks(text: string) {
  /** 会話カードエディタ下位互換性対応: <br>\nを空行扱いとして変換する */
  return text.replace(/<br\s*\/?>\n/g, '\n\n')
}

export const filterMarkdown = (markdown?: string) => {
  if (!markdown) {
    return ''
  }
  const html = convertFromMarkdownToHtml(markdown)
  return convertFromHtmlToMarkdown(html)
}

export const hasMarkdown = (markdownText?: string) => {
  if (!markdownText) {
    return false
  }
  /** カラクリのSkillの書式が入力された場合には、Markdownとして扱わずプレーンテキストとして扱う */
  if (KARAKURI_SKILL_PLAIN_TEXT_REGEX.test(markdownText)) {
    return false
  }
  return markdownText !== convertFromMarkdownAndHtmlToPlainText(markdownText)
}

/**
 *  会話カードエディタ下位互換性対応: PlainText形式で保存されている回答文をMarkdown形式に変換するかどうかの判定関数.
 */
export const isMarkdownConversionRequired = (contentFormat?: string, text?: string) => {
  if (!text) {
    return false
  }
  /** カラクリのSkillの書式が入力された場合には、プレーンテキストとして扱うため変換しない*/
  if (KARAKURI_SKILL_PLAIN_TEXT_REGEX.test(text)) {
    return false
  }
  return contentFormat !== 'markdown'
}

/**
 *  会話カードエディタ下位互換性対応: PlainText形式で保存されているテキストのMarkdown表記をエスケープしたMarkdown形式に変換する.
 */
function unifiedEscapedMarkdown(markdown: string) {
  /** エスケープすべき記号定義. */
  const extraUnsafe = [
    ...['*', '_', '~', '`', '!', '[', ']', '(', ')', '<', '>'].map(ch => ({ character: ch })),
    { character: '#', atBreak: true },
    { character: '.', before: ' \\d', atBreak: true, after: '[\\t ]' },
    { character: '+', atBreak: true, after: '[\\t ]' },
    { character: '-', atBreak: true, after: '[\\t ]' },
  ]
  const escapedMarkdown = unified()
    /** Markdown  のフォーマットを無視して全て text typeとしてパース処理する. */
    .use(function remarkPlainTextParse() {
      this.Parser = (doc: string) => ({
        type: 'root',
        children: [
          {
            type: 'text',
            value: doc,
          },
        ],
      })
    })
    /** Markdown の書式に利用される記号をエスケープする. */
    .use(remarkStringify, {
      commonmark: true,
      unsafe: extraUnsafe,
    } as Options)
    .processSync(markdown)
    .toString()
  return escapedMarkdown
}

/**
 * Unifiedを使用してHTMLをMarkdownに変換する.
 *
 * 以下の流れでHTMLをMarkdown化する:
 * - 1. rehypeParse: HTMLからHASTに変換.
 * -2. rehypeSanitize: Editorで許可しているHTMLタグのみを許可するように設定.
 * -3. rehypeRemark: HASTからMDASTに変換.その際に span タグが除去されないように 独自handlerを指定.
 * - 4. remarkGfm: delタグの変換をサポート.
 * - 5. remarkStringify: MDASTからMarkdownに変換.
 */
function unifiedFromHtmlToMarkdown(html?: string) {
  const markdown = unified()
    .use(rehypeParse, { fragment: true })
    .use(rehypeSanitize, allowedEditorInputSchema)
    .use(rehypeHexColor)
    .use(rehypeRemoveNoAttributeSpanTag)
    .use(rehypeRemark, {
      handlers: {
        span: spanTagHandler,
      },
    } as Options)
    .use(remarkGfm)
    .use(remarkStringify)
    .processSync(html)
    .toString()
  return markdown
}

/**
 * Unifiedを使用してMarkdownをHTMLに変換する.
 *
 * 以下の流れでMarkdownをHTML化する:
 * - 1. remarkParse: MarkdownからMDASTに変換.
 * - 2. remarkGfm: Github Formatをサポート対象に追加.
 * - 3. remarkRehype: MDASTからHASTに変換.その際に span タグが除去されないように allowDangerousHtml を設定.
 * - 4. rehypeRaw: HTMLをパースするためのプラグインを追加
 * - 5. rehypeSanitize: Editorで許可しているHTMLタグのみを許可するように設定.
 * - 6. rehypeStringify: HASTからHTMLに変換.
 *
 * @see  https://commonmark.org/help/
 * @see  https://github.com/remarkjs/remark-gfm
 */
const unifiedFromMarkdownToHtml = (markdown?: string) => {
  const html = unified()
    .use(remarkParse)
    .use(remarkDel)
    .use(remarkLists)
    // 後続でrehypeSanitize処理を行うため、dangerousHtmlを許可.
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .use(rehypeSanitize, allowedEditorInputSchema)
    .use(rehypeRemoveNoAttributeSpanTag)
    .use(rehypeStringify)
    .processSync(markdown)
    .toString()
  return html
}

/**
 * MarkdownとHTMLをプレーンテキストに変換（Markup言語の除去）する.
 *
 *  リンクのみ特殊表記で残す.
 *
 * 以下の流れでMarkdownとHTMLをPlainText化（Markup言語の除去）する:
 *  - 1. remarkParse: Markdown/HTMLからMDASTに変換.
 *  - 2. remarkGfm: Github Formatをサポート対象に追加.
 *  - 3. remarkRehype: MDASTからHASTに変換.
 *  - 4. rehypeSanitize: aタグを除く全てのHTMLタグをサニタイズ対象に設定.
 *  - 5. rehypeConvertFromATagToText: aタグを`アンカーテキスト[URL]` に変換.
 *  - 6. rehypeStringify: HASTをテキスト出力（全てのタグが除去されるため、結果PlainTextに変換）.
 */
const unifiedRemoveMarkdownAndHtmlMarkup = (markupText?: string) => {
  return unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype)
    .use(rehypeSanitize, allowedPlainTextSchema)
    .use(rehypeConvertFromATagToText)
    .use(rehypeStringify)
    .processSync(markupText)
    .toString()
}

/**
 * Excel/Googleスプレッドシートのペースト時のHTMLから<br>タグを以外を除去する.
 */
export const unifiedRemoveMsoCellMarkup = (html: string) => {
  return unified()
    .use(rehypeParse, { fragment: true })
    .use(remarkStripHtmlComments)
    .use(rehypeSanitize, allowedPastedMsoCellSchema)
    .use(rehypeStringify, {
      characterReferences: {
        useNamedReferences: true,
        useShortestReferences: true,
      },
    })
    .processSync(html)
    .toString()
}

export function convertFromMarkdownToHtmlLineBreaks(text: string) {
  /**
   * 会話カードエディタ下位互換性対応: 旧エディタの見出し1~3直後の空白行を除去する.
   *
   * 旧エディタの見出しでは \n\n<br />\n の形式にて見出し後に空白行が入った際に空白行を削除する仕様とのことなので該当のパターンの時に空白行を削除する処理を入れる.
   *  https://app.asana.com/1/1115147318509184/project/1206969296714629/task/1210087947283985?focus=true
   */
  text = text.replace(/^(#{1,3}[ \t].*?)\n\n<br\s*\/?>\n/gm, '$1\n\n')
  /** 会話カードエディタ下位互換性対応: <br>\nを空行扱いとして変換する */
  const lines = text.split(/\n\n|<br\s*\/?>\n/)
  const normalizedLines = lines.map(line => {
    if (line.trim() === '') {
      /** 空行は unified の仕様により除去されるため、除去されないように ero width space と改行x2に変換 */
      return `\u200B\n\n`
    } else {
      /** 通常の行には改行x2を追加 */
      return line.trimEnd() + '\n\n'
    }
  })
  return normalizedLines.join('') // 変更後の行を結合して一つの文字列に戻す
}

function addZeroWidthSpaceToEmptyParagraphs(html: string): string {
  return html.replace(/<p><\/p>/g, '<p>\u200B</p>')
}

function removeMarkdownEscape(text: string): string {
  return text.replace(/\\/g, '')
}

function removeZeroWidthSpace(html: string): string {
  return html.replace(/\u200B/g, '')
}

/**
 * HTML to Markdown後に、Markdownの改行コード数を調整する
 */
function convertFromHtmlToMarkdownLineBreaks(markdown: string): string {
  const lines = markdown.split(/\n\n/)
  const isListItem = (line: string): boolean => {
    const bulletListRegex = /^\s*[-+*]\s+/
    const orderListRegex = /^\s*\d+\.\s+/
    return bulletListRegex.test(line) || orderListRegex.test(line)
  }

  let prevWasList = false
  const normalizedLines = lines.map(line => {
    if (isListItem(line)) {
      /** リスト項目は改行x1を追加 */
      prevWasList = true
      return line.trimEnd() + '\n'
    } else {
      /** 通常の行には改行x2を追加 */
      const prefix = prevWasList ? '\n' : ''
      prevWasList = false
      return prefix + line.trimEnd() + '\n\n'
    }
  })
  return normalizedLines.join('') // 変更後の行を結合して一つの文字列に戻す
}

/**
 * spanタグとその子要素をMdast形式のNode群に変換する.
 */
const spanTagHandler = (state: State, element: Element) => {
  /** rehypeSanitize で spanタグの attribute のバリデーションが行われるため、ここではチェック済みとして扱う.*/
  const attributes = Object.entries(element.properties || {})
    .map(([key, value]) => `${key}="${value}"`)
    .join(' ')

  const startNode = { type: 'html', value: attributes ? `<span ${attributes}>` : `<span>` }
  const childrenNodes = element.children.map(child => toMdast(child, state))
  const endNode = { type: 'html', value: `</span>` }

  return [startNode, ...childrenNodes, endNode]
}
